<script lang="ts">
    import type { Lead } from '@/types/types';
    import LeadCard from './LeadCard.svelte';
    import DropZone from './DropZone.svelte';
    import { Plus } from 'lucide-svelte';

    type StatusOption = {
        key: string;
        label: string;
        color: string;
        cssClass: string;
    };

    type Props = {
        leadsByStatus: Record<string, Lead[]>;
        statuses: StatusOption[];
        onColumnDrop: (leadId: string, newStatus: string) => void;
        onDeleteDrop: (leadId: string) => void;
        onArchiveDrop: (leadId: string) => void;
        onCardClick: (lead: Lead) => void;
        onAddNew?: () => void;
    };

    let { leadsByStatus, statuses, onColumnDrop, onDeleteDrop, onArchiveDrop, onCardClick, onAddNew }: Props = $props();

    // Column value totals
    const statusesWithValueTotals = ['tendered', 'closed_won', 'closed_lost', 'booked', 'invoiced', 'extra_hire', 'awaiting_takedown'];

    let estimatedValueByStatus = $derived(
        statusesWithValueTotals.reduce(
            (acc, statusKey) => {
                const statusLeads = leadsByStatus[statusKey] || [];
                const total = statusLeads.reduce((sum, lead) => {
                    const value = lead.estimated_value ? parseFloat(String(lead.estimated_value)) : 0;
                    return sum + (isNaN(value) ? 0 : value);
                }, 0);
                acc[statusKey] = total;
                return acc;
            },
            {} as Record<string, number>,
        ),
    );

    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    // Drag state
    let dragOverColumn = $state<string | null>(null);
    let kanbanContainer: HTMLDivElement;

    // Auto-scroll during drag
    let scrollAnimationId: number | null = null;
    let scrollSpeed = 0;
    let lastScrollTime = 0;
    const SCROLL_THRESHOLD = 240;
    const MAX_SCROLL_SPEED = 2600;

    function handleDragStart(event: DragEvent, lead: Lead) {
        if (!event.dataTransfer) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('leadId', lead.id);
    }

    function handleDragOver(event: DragEvent, status: string) {
        event.preventDefault();
        if (!event.dataTransfer) return;
        event.dataTransfer.dropEffect = 'move';
        dragOverColumn = status;
    }

    function handleDragLeave() {
        dragOverColumn = null;
    }

    function handleDrop(event: DragEvent, newStatus: string) {
        event.preventDefault();
        dragOverColumn = null;
        const leadId = event.dataTransfer?.getData('leadId');
        if (leadId) onColumnDrop(leadId, newStatus);
    }

    function handleDrag(event: DragEvent) {
        if (!kanbanContainer) return;

        const rect = kanbanContainer.getBoundingClientRect();
        const mouseX = event.clientX;

        if (mouseX < rect.left + SCROLL_THRESHOLD && mouseX > 0) {
            const proximity = 1 - (mouseX - rect.left) / SCROLL_THRESHOLD;
            scrollSpeed = -MAX_SCROLL_SPEED * proximity;
        } else if (mouseX > rect.right - SCROLL_THRESHOLD && mouseX < window.innerWidth) {
            const proximity = 1 - (rect.right - mouseX) / SCROLL_THRESHOLD;
            scrollSpeed = MAX_SCROLL_SPEED * proximity;
        } else {
            scrollSpeed = 0;
        }

        if (scrollSpeed !== 0 && scrollAnimationId === null) {
            lastScrollTime = performance.now();
            scrollAnimationId = requestAnimationFrame(scrollStep);
        } else if (scrollSpeed === 0) {
            stopScrolling();
        }
    }

    function scrollStep(timestamp: number) {
        if (!kanbanContainer || scrollSpeed === 0) {
            stopScrolling();
            return;
        }
        const delta = (timestamp - lastScrollTime) / 1000;
        lastScrollTime = timestamp;
        kanbanContainer.scrollLeft += scrollSpeed * delta;
        scrollAnimationId = requestAnimationFrame(scrollStep);
    }

    function stopScrolling() {
        if (scrollAnimationId !== null) {
            cancelAnimationFrame(scrollAnimationId);
            scrollAnimationId = null;
        }
        scrollSpeed = 0;
    }

    function handleDragEnd() {
        stopScrolling();
    }
</script>

<div
    class="kanban-board"
    bind:this={kanbanContainer}
    style="grid-template-columns: 120px repeat({statuses.length}, 1fr) 120px"
>
    <DropZone variant="delete" onLeadDrop={onDeleteDrop} />

    {#each statuses as status, i (status.key)}
        {@const isLast = i === statuses.length - 1}
        <div
            class="kanban-column"
            class:drag-over={dragOverColumn === status.key}
            ondragover={(e) => handleDragOver(e, status.key)}
            ondragleave={handleDragLeave}
            ondrop={(e) => handleDrop(e, status.key)}
            role="region"
            aria-label="{status.label} column"
        >
            <div class="column-header" class:no-arrow={isLast} style="--col-status: var(--{status.cssClass})">
                <h3 class="column-title">{status.label}{#if status.key === 'booked'} <span class="text-xs font-normal text-muted-foreground"> (creates job)</span>{/if}</h3>
                <span class="count">
                    {leadsByStatus[status.key]?.length || 0}{#if statusesWithValueTotals.includes(status.key)}
                        <span class="pl-2 estimated-total">({formatCurrency(estimatedValueByStatus[status.key] || 0)})</span>
                    {/if}
                </span>
            </div>
            {#if status.key === 'new' && onAddNew}
                <button type="button" class="add-new-button" onclick={onAddNew}>
                    <Plus size={16} />
                    <span>Add New Lead</span>
                </button>
            {/if}

            <div class="column-content">
                {#each leadsByStatus[status.key] || [] as lead (lead.id)}
                    <LeadCard
                        {lead}
                        statusClass={status.cssClass}
                        ondragstart={(e) => handleDragStart(e, lead)}
                        ondrag={handleDrag}
                        ondragend={handleDragEnd}
                        onclick={() => onCardClick(lead)}
                    />
                {/each}
            </div>
        </div>
    {/each}

    <DropZone variant="archive" onLeadDrop={onArchiveDrop} />
</div>

<style lang="scss">
    .kanban-board {
        display: grid;
        grid-template-rows: 1fr;
        gap: 1rem;
        padding: 1rem 0;
        flex: 1;
        min-height: 0;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .kanban-column {
        display: flex;
        flex-direction: column;
        background: hsl(var(--clr-surface-2));
        border-radius: 8px;
        min-width: 280px;
        min-height: 0;
        transition: background 200ms;

        &.drag-over {
            background: hsl(var(--clr-surface-3));
            outline: 2px dashed hsl(var(--clr-primary));
        }
    }

    .column-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        border-radius: 6px;
        position: relative;
        background: hsl(var(--col-status) / 0.3);
        border-bottom: 1px solid hsl(var(--col-status) / 0.6);

        &:not(.no-arrow)::after {
            content: '';
            position: absolute;
            right: -12px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-top: 24px solid transparent;
            border-bottom: 24px solid transparent;
            border-left: 12px solid hsl(var(--col-status) / 0.3);
            z-index: 1;
        }
    }

    .column-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: hsl(var(--clr-on-surface-1));
        margin: 0;
    }

    .estimated-total {
        font-weight: 500;
        opacity: 0.85;
    }

    .count {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5rem;
        height: 1.5rem;
        padding: 0 0.5rem;
        background: hsl(var(--clr-surface-3));
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 600;
        color: hsl(var(--clr-on-surface-2));
    }

    .column-content {
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            width: 6px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 3px;
        }
    }

    .kanban-column:hover .column-content {
        scrollbar-width: thin;

        &::-webkit-scrollbar-thumb {
            background: hsl(var(--clr-on-surface-2) / 0.3);
        }

        &::-webkit-scrollbar-thumb:hover {
            background: hsl(var(--clr-on-surface-2) / 0.5);
        }
    }

    .add-new-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.75rem 1rem;
        background: hsl(var(--clr-primary));
        color: hsl(var(--clr-on-primary));
        border: none;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 200ms;

        &:hover {
            background: hsl(var(--clr-primary-hover));
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }

        &:active {
            transform: translateY(0);
        }
    }
</style>
